import dayjs from 'dayjs';
import jsonToCsv from 'json-to-csv-export';
import type * as pdfMakeTypes from 'pdfmake/interfaces'; // eslint-disable-line node/no-missing-import
import { call, select, SagaGenerator } from 'typed-redux-saga';
import {
  dossiersApiClient,
  findingsApiClient,
  uploadsApiClient,
  UPLOAD_TIMEOUT_LIMIT,
} from '@owl-frontend/api-client';
import type {
  Dossier,
  ListDossier,
  ReservationResponse,
} from '@owl-frontend/api-client/interface';
import { PRETTY_DATE_FORMAT } from '@owl-frontend/components';
import { asyncActionStateMatchers, invoke } from '@owl-frontend/redux';
import dossiersSlice from './interface';
import { generateDocumentDefinition } from './PdfUtil';

export type DossiersListPayload = {
  lastCursor: string | null;
  dossiers: Dossier[];
};

export type DossiersListAllPayload = {
  lastCursor: string | null;
  dossiers: ListDossier[];
};

async function downloadPdf(
  docDefinition: pdfMakeTypes.TDocumentDefinitions,
  dossier: Dossier
) {
  const pdfMake = (await import('./PdfMake')).default;
  const dossierName = `${dossier.data.names[0].firstName}-${dossier.data.names[0].lastName}`;
  pdfMake
    .createPdf(docDefinition)
    .download(`${dossier.clientDossierId}_${dossierName}.pdf`);
}

function* downloadDossierData(action: {
  payload: {
    dossierId: string;
    fields: string[];
    fieldsDismissed: string[];
    fieldsInformational: string[];
  };
}): SagaGenerator<void> {
  const { dossierId, fields, fieldsDismissed, fieldsInformational } =
    action.payload;
  const messages = yield* select((s) => s['localization'].messages);
  const screenshotPermission = yield* select(
    (s) => s['session'].features.screenshot
  );
  const dossier: Dossier = yield* call(dossiersApiClient.dossiers.get, {
    dossierId,
  });

  const findings = yield* call(findingsApiClient.findings.list, {
    dossierId,
  });
  const findingsSortedByEventDateDesc = [...findings].sort((a, b) =>
    dayjs(b.eventDate).diff(a.eventDate)
  );
  const docDefinition = yield* call(
    generateDocumentDefinition,
    dossier,
    findingsSortedByEventDateDesc,
    fields,
    fieldsDismissed,
    fieldsInformational,
    messages,
    screenshotPermission
  );
  yield* call(downloadPdf, docDefinition, dossier);
}

const LARGE_FILE_THRESHOLD = 500 * 1024 * 1000; // ~ 500 MB
const CHUNK_SIZE = 100 * 1024 * 1000; // 100 MB

async function uploadLargeFile(data: {
  file: File;
  isReinvestigation?: boolean;
}) {
  const { file, isReinvestigation } = data;
  const { uploadId } =
    await dossiersApiClient.dossiers.generateMultipartUploadId({
      fileName: file.name,
      isReinvestigation,
    });

  const chunks: [ArrayBuffer, number][] = [];
  for (
    let i = 0, partNumber = 1;
    i < file.size - 1;
    i += CHUNK_SIZE, partNumber++
  ) {
    const blob = file.slice(i, CHUNK_SIZE + i);
    chunks.push([await blob.arrayBuffer(), partNumber]);
  }

  const presignedUrls = await Promise.all(
    chunks.map(async ([chunk, partNumber]) => {
      const { url } =
        await dossiersApiClient.dossiers.getPresignedUploadMultipart({
          fileName: file.name,
          isReinvestigation,
          partNumber,
          uploadId,
        });

      return {
        chunk,
        partNumber,
        url,
      };
    })
  );

  const uploadedParts = await Promise.all(
    presignedUrls.map(({ chunk, partNumber, url }) =>
      uploadsApiClient.dossiers.uploadPart({
        chunk,
        partNumber,
        presignedUrl: url,
      })
    )
  );

  return await dossiersApiClient.dossiers.completeMultipartUpload({
    fileName: file.name,
    isReinvestigation,
    uploadId,
    uploadedParts,
  });
}

const VALID_FILE_TYPES = ['text/csv', 'application/zip'];
function* uploadFile(action: { payload: { file: File } }): SagaGenerator<void> {
  const { file } = action.payload;

  if (!VALID_FILE_TYPES.includes(file.type)) {
    throw new Error('Invalid file type!');
  }

  if (file.size >= LARGE_FILE_THRESHOLD) {
    yield* call(uploadLargeFile, { file });
    return;
  }

  const presigned = yield* call(dossiersApiClient.dossiers.getPresignedUpload, {
    filename: file.name,
    isReinvestigation: false,
  });

  yield* call(uploadsApiClient.dossiers.uploadS3, {
    presigned: { ...presigned, file },
  });
}

function* uploadFileReinvestigation(action: {
  payload: { file: File };
}): SagaGenerator<void> {
  const { file } = action.payload;

  if (file.size >= LARGE_FILE_THRESHOLD) {
    yield* call(uploadLargeFile, { file, isReinvestigation: true });
    return;
  }

  const presigned = yield* call(dossiersApiClient.dossiers.getPresignedUpload, {
    filename: file.name,
    isReinvestigation: true,
  });
  yield* call(uploadsApiClient.dossiers.uploadS3, {
    presigned: { ...presigned, file },
  });
}

function* downloadDossiers(action: {
  payload: {
    conclusion: Dossier['conclusion'] | 'closed';
    startTime?: string;
    endTime?: string;
    includeFindingsCsvData?: boolean;
  };
}): SagaGenerator<{ numDownloaded: number }> {
  const { conclusion, startTime, endTime, includeFindingsCsvData } =
    action.payload;
  let result: Dossier[] = [];
  let cursor: string | null = '';

  while (true) {
    if (conclusion === 'closed') {
      const { dossiers, lastCursor } = yield* call(
        dossiersApiClient.dossiers.list,
        {
          cursor,
          status: 'closed',
          order: 'rank',
        }
      );

      result = result.concat(dossiers);
      cursor = lastCursor;
    } else {
      const { dossiers, lastCursor } = yield* call(
        dossiersApiClient.dossiers.list,
        {
          cursor,
          conclusion,
          startTime,
          endTime,
        }
      );

      result = result.concat(dossiers);
      cursor = lastCursor;
    }

    if (!cursor) {
      break;
    }
  }

  yield* call(jsonToCsv, {
    data: result.map((d) => {
      let entry: {
        [headerField: string]: string;
      } = {
        ID: d.clientDossierId ?? '-',
        Name: `${d.data.names[0].firstName} ${d.data.names[0].lastName}`,
        Reviewer: d.updatedByName!,
      };

      if (conclusion === 'closed' || conclusion === 'dismissed') {
        entry = {
          ...entry,
          'Conclusion reason': d.conclusionDetails?.reason ?? '-',
        };
      }

      if (conclusion === 'accepted' || conclusion === 'closed') {
        entry = {
          ...entry,
          'Conclusion comment': d.conclusionDetails?.comment ?? '-',
        };
      }

      entry = {
        ...entry,
        Findings: `${d.numFindingsTotal}`,
      };

      if (includeFindingsCsvData) {
        entry = {
          ...entry,
          'Accepted Findings':
            (d.metadata?.findingsData ?? [])
              .filter((f) => f.decision === 'accepted')
              .map((f) => f.eventType)
              .join(', ') ?? '',
          'Dismissed Findings':
            (d.metadata?.findingsData ?? [])
              .filter((f) => f.decision === 'rejected')
              .map((f) => f.eventType)
              .join(', ') ?? '',
          'Informational Findings':
            (d.metadata?.findingsData ?? [])
              .filter((f) => f.decision === 'informational')
              .map((f) => f.eventType)
              .join(', ') ?? '',
        };
      }

      if (d.closeDate) {
        entry = {
          ...entry,
          'Close date': dayjs(d.closeDate).format(PRETTY_DATE_FORMAT),
        };
      }

      return {
        ...entry,
        'Date added': dayjs(d.createdAt).format(PRETTY_DATE_FORMAT),
        'Reviewed on': dayjs(d.updatedAt).format(PRETTY_DATE_FORMAT),
      };
    }),
    filename: `owl-report--${conclusion}.csv`,
    delimiter: ',',
  });

  return { numDownloaded: result.length };
}

// legacy

function* dossiersList(action: {
  payload: {
    tab: 'accepted' | 'dismissed' | 'undecided' | 'closed';
    conclusion?: Dossier['conclusion'];
    status?: Dossier['status'];
    order?: string;
    clientDossierId?: string;
    username?: string;
    owner?: string;
    cursor?: string;
    limit?: number;
    startTime?: string;
    endTime?: string;
  };
}): SagaGenerator<{
  tab: 'accepted' | 'dismissed' | 'undecided' | 'closed';
  items: DossiersListPayload;
}> {
  const {
    tab,
    conclusion,
    status,
    order,
    clientDossierId,
    username,
    owner,
    cursor,
    limit = 100,
    startTime,
    endTime,
  } = action.payload;
  return {
    tab,
    items: yield* call(dossiersApiClient.dossiers.list, {
      limit,
      clientDossierId: clientDossierId?.trim(),
      username: username?.trim(),
      owner: owner?.trim(),
      cursor,
      conclusion,
      order,
      status,
      startTime,
      endTime,
    }),
  };
}

function* dossiersPendingList(action: {
  payload: {
    clientDossierId?: string;
    username?: string;
    owner?: string;
    cursor?: string;
    limit?: number;
  };
}): SagaGenerator<{
  items: DossiersListPayload;
}> {
  const {
    clientDossierId,
    username,
    owner,
    cursor,
    limit = 500,
  } = action.payload;

  return {
    items: yield* call(dossiersApiClient.dossiers.list, {
      limit,
      clientDossierId: clientDossierId?.trim(),
      username: username?.trim(),
      owner: owner?.trim(),
      cursor,
      conclusion: 'undecided',
      pending: true,
    }),
  };
}

function* dossiersCount(action: {
  payload?: {
    tab: 'accepted' | 'dismissed' | 'undecided' | 'closed';
    conclusion?: Dossier['conclusion'];
    status?: Dossier['status'];
    order?: string;
    clientDossierId?: string;
    username?: string;
    owner?: string;
    startTime?: string;
    endTime?: string;
  };
}): SagaGenerator<{
  tab;
  count: number;
}> {
  const {
    tab,
    conclusion,
    clientDossierId,
    owner,
    username,
    startTime,
    endTime,
  } = action.payload ?? {};
  return {
    tab,
    count:
      (yield* call(dossiersApiClient.dossiers.list, {
        conclusion,
        clientDossierId: clientDossierId?.trim(),
        username: username?.trim(),
        owner: owner?.trim(),
        startTime,
        endTime,
        includeData: false,
        includeAggCount: true,
      })).count ?? 0,
  };
}

function* peekReservation(action: {
  payload: {
    groupId: string;
    isListView: boolean;
    shouldStartReview?: boolean;
  };
}): SagaGenerator<ReservationResponse> {
  const { groupId, isListView } = action.payload;

  if (isListView) {
    return yield* call(dossiersApiClient.reservations.getNextReviewItem, {
      groupId,
    });
  }

  return yield* call(dossiersApiClient.reservations.peekReservation, {
    groupId,
  });
}

function* dossiersListByStatus(action: {
  payload: {
    status: 'all' | 'ready' | 'done';
    lastCursor?: string;
    filterParams?: {
      clientDossierId?: string;
      name?: string;
      owner?: string;
      conclusion?: Dossier['conclusion'];
      isOwner?: boolean;
      isReviewedByUser?: boolean;
      priorityType?: string;
    };
  };
}): SagaGenerator<{
  lastCursor: string | null;
  dossiers: Dossier[];
  count?: number;
}> {
  const { status, lastCursor, filterParams } = action.payload;

  const filters = filterParams || {};
  if (status === 'done') {
    filters.isReviewedByUser = true;
  }

  return yield* call(dossiersApiClient.dossiers.listByStatus, {
    status,
    lastCursor,
    filterParams: filters,
  });
}

// end

const slice = dossiersSlice
  .addAsyncSagas(
    {
      uploadFile,
      uploadFileReinvestigation,
    },
    {
      timeoutLimit: UPLOAD_TIMEOUT_LIMIT,
    }
  )
  .addAsyncSagas(
    {
      dossiersListByStatus,
    },
    {
      timeoutLimit: -1,
    }
  )
  .addAsyncSagas({
    dossiersListAll: invoke(dossiersApiClient.dossiers.listAll),
    downloadDossierData,
    peekReservation,
    startReview: invoke(dossiersApiClient.reservations.startReview),
    startReviewById: invoke(dossiersApiClient.reservations.startReviewById),
    downloadDossiers,
    // legacy
    dossiersPendingList,
    dossiersList,
    dossiersCount,
    // end
  })
  .addReducers({
    'dossiersListAll/fulfilled': (state, action) => {
      state.items = Object.values(
        [...state.items, ...action.payload.dossiers].reduce(
          (acc, curr) => ({ ...acc, [curr.dossierId]: curr }),
          {}
        )
      );
      state.lastCursor = action.payload.lastCursor;
    },
    'dossiersListByStatus/fulfilled': (state, action) => {
      state.items = Object.values(
        [...state.items, ...action.payload.dossiers].reduce(
          (acc, curr) => ({ ...acc, [curr.dossierId]: curr }),
          {}
        )
      );
      state.lastCursor = action.payload.lastCursor;
    },
    'startReview/fulfilled': (state, action) => {
      const { data, recordId, groupId } = action.payload;

      state.reservedDossier = {
        reservedDossierId: data?.dossierId,
        groupId,
        recordId,
      };
    },
    'startReviewById/fulfilled': (state, action) => {
      const { data, recordId, groupId } = action.payload;

      state.reservedDossier = {
        reservedDossierId: data?.dossierId,
        groupId,
        recordId,
      };
    },
    'peekReservation/fulfilled': (state, action) => {
      state.priorityDossierId = action.payload.recordId;
      state.priorityDossierGroupId = action.payload.groupId;
      state.peekShouldStartReview = !!action.meta.arg.shouldStartReview;
      state.reservationStatus[action.meta.arg.groupId] = action.payload;
    },
    // legacy
    'dossiersPendingList/fulfilled': (state, action) => {
      const { items } = action.payload;
      const { dossiers, lastCursor } = items;
      const set = [...state['undecided'].items, ...dossiers].reduce(
        (acc, curr) => {
          return {
            ...acc,
            [curr.dossierId]: curr,
          };
        },
        {}
      );
      state['undecided'].items = Object.values(set);
      state['undecided'].lastCursor = lastCursor;
    },
    'dossiersList/fulfilled': (state, action) => {
      const { tab, items } = action.payload;
      const { dossiers, lastCursor } = items;
      const set: { [k: string]: Dossier } = [
        ...state[tab].items,
        ...dossiers,
      ].reduce((acc, curr) => {
        return {
          ...acc,
          [curr.dossierId]: curr,
        };
      }, {});
      const result = Object.values(set);
      if (tab === 'closed') {
        result.sort((a, b) => {
          return (
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
          );
        });
      }

      state[tab].items = result;
      state[tab].lastCursor = lastCursor;
    },
    'dossiersCount/fulfilled': (state, action) => {
      const { tab, count } = action.payload;
      state.count[tab] = count;
    },
    // legacy end
  })
  .addReducers({
    clearItems: (state) => {
      state.items = [];
      state.lastCursor = null;
      // legacy
      state.accepted.items = [];
      state.accepted.lastCursor = null;
      state.dismissed.items = [];
      state.dismissed.lastCursor = null;
      state.closed.items = [];
      state.closed.lastCursor = null;
    },
    clearPendingItems: (state) => {
      state.undecided.items = [];
      state.undecided.lastCursor = null;
    },
    // end
  });
export const actions = slice.actions;
export default slice.addExtra(asyncActionStateMatchers(actions).all());
